/**
 * Eventi di defense.community pubblicati su girlsdefense.it.
 * Si gestiscono nell'admin di events.defense.community con il flag
 * "Pubblica su girlsdefense.it"; ogni evento rimanda alla sua pagina lì.
 *
 * Le date sono l'ora italiana "da muro" salvata come UTC: si formattano
 * sempre con timeZone 'UTC'.
 */
export const COMMUNITY_API = 'https://api.defense.community/api'
export const COMMUNITY_EVENTI = 'https://events.defense.community'

const isEventoApi = (x) =>
  typeof x === 'object' &&
  x !== null &&
  typeof x.id === 'string' &&
  typeof x.title === 'string' &&
  typeof x.date === 'string'

/** Mezzanotte di oggi in Italia, nello stesso formato "UTC" delle date evento. */
export const inizioDiOggi = (adesso = new Date()) => {
  const giorno = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome'
  }).format(adesso)
  return new Date(`${giorno}T00:00:00Z`).getTime()
}

const daApi = (e) => {
  const minimo = (e.ticketTypes || [])
    .filter((t) => typeof t.price === 'number')
    .reduce((min, t) => (!min || t.price < min.price ? t : min), null)
  return {
    id: e.id,
    title: e.title,
    summary: e.summary || '',
    image: e.imageUrl || null,
    start: e.date,
    end: e.endDate || null,
    location: e.location || '',
    organizer: e.organizer || '',
    minPrice: minimo
      ? { amount: minimo.price, currency: minimo.currency || 'EUR' }
      : null,
    url: `${COMMUNITY_EVENTI}/events/${e.id}`
  }
}

/** Eventi attivi, flaggati per girlsdefense.it, non ancora conclusi, in ordine di data. */
export const eventiDaApi = (dati, adesso = new Date()) => {
  if (!Array.isArray(dati)) throw new Error('API eventi: risposta inattesa')
  const da = inizioDiOggi(adesso)
  return dati
    .filter(isEventoApi)
    .filter((e) => e.status === 'active' && e.publishOnGirlsDefense === true)
    .filter((e) => new Date(e.endDate || e.date).getTime() >= da)
    .map(daApi)
    .sort((a, b) => a.start.localeCompare(b.start))
}

export const caricaEventiGirlsDefense = async () => {
  const risposta = await fetch(
    `${COMMUNITY_API}/events?status=active&publishOnGirlsDefense=true`
  )
  if (!risposta.ok) throw new Error(`API eventi: ${risposta.status}`)
  return eventiDaApi(await risposta.json())
}

const giorno = new Intl.DateTimeFormat('it-IT', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC'
})
const giornoBreve = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC'
})
const ora = new Intl.DateTimeFormat('it-IT', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC'
})

/**
 * "sabato 24 ottobre 2026, 09:00–18:00" per un giorno solo,
 * "31 ott 2026 → 31 gen 2027" per un evento su più giorni.
 */
export const quandoEvento = (e) => {
  const inizio = new Date(e.start)
  if (e.end && e.end.slice(0, 10) !== e.start.slice(0, 10)) {
    return `${giornoBreve.format(inizio)} → ${giornoBreve.format(new Date(e.end))}`
  }
  const orario = e.end
    ? `${ora.format(inizio)}–${ora.format(new Date(e.end))}`
    : ora.format(inizio)
  return `${giorno.format(inizio)}, ${orario}`
}

/** Data breve per la barra "prossimo evento": "24 ott 2026". */
export const giornoEvento = (e) => giornoBreve.format(new Date(e.start))

/** "Gratuito", "Da 35€", "Da 69,50€". */
export const prezzoEvento = (e) => {
  if (!e.minPrice) return null
  const { amount, currency } = e.minPrice
  if (amount === 0) return 'Gratuito'
  const cifra =
    currency === 'EUR'
      ? `${Number.isInteger(amount) ? amount : amount.toFixed(2).replace('.', ',')}€`
      : new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(
          amount
        )
  return `Da ${cifra}`
}

/** Offset di Europe/Rome per una data "da muro", es. "+02:00". */
const offsetRoma = (iso) => {
  const parti = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Rome',
    timeZoneName: 'longOffset'
  }).formatToParts(new Date(iso))
  const nome = parti.find((p) => p.type === 'timeZoneName')?.value || 'GMT'
  return nome === 'GMT' ? '+00:00' : nome.replace('GMT', '')
}

/** Data ISO con l'offset italiano: "2026-10-24T09:00:00+02:00". */
export const isoRoma = (iso) => `${iso.slice(0, 19)}${offsetRoma(iso)}`
